import { useEffect, useState, useCallback } from "react";

// Theme: "dark" | "light" | "system"
//   - "system" = follow the OS prefers-color-scheme; updates live when the
//     OS theme changes.
//   - "dark" / "light" = explicit, persisted to localStorage.
//
// First-paint application happens BEFORE React mounts, in the inline script
// in index.html — that's why there's no flash on hard refresh. This hook
// only manages user-driven changes.

const STORAGE_KEY = "zcd_theme";

function getSystemTheme() {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

function getInitialPref() {
  try {
    return localStorage.getItem(STORAGE_KEY) || "system";
  } catch {
    return "system";
  }
}

function resolve(pref) {
  return pref === "system" ? getSystemTheme() : pref;
}

function apply(resolved) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", resolved);
}

export default function useTheme() {
  const [pref, setPrefState] = useState(getInitialPref);
  const [resolved, setResolved] = useState(() => resolve(getInitialPref()));

  // Persist + apply on user change
  const setPref = useCallback((nextPref) => {
    setPrefState(nextPref);
    try {
      if (nextPref === "system") {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, nextPref);
      }
    } catch {
      /* localStorage unavailable — in-memory only */
    }
    const next = resolve(nextPref);
    setResolved(next);
    apply(next);
  }, []);

  // Cycle: dark → light → system → dark…
  const cycle = useCallback(() => {
    const order = ["dark", "light", "system"];
    const i = order.indexOf(pref);
    setPref(order[(i + 1) % order.length]);
  }, [pref, setPref]);

  // Live-follow OS preference when pref === "system"
  useEffect(() => {
    if (pref !== "system" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => {
      const next = mq.matches ? "light" : "dark";
      setResolved(next);
      apply(next);
    };
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [pref]);

  return {
    pref,        // user's choice: "dark" | "light" | "system"
    resolved,    // currently-applied theme: "dark" | "light"
    setPref,     // (next: "dark" | "light" | "system") => void
    cycle,       // cycle through dark → light → system
  };
}
